import { useMemo } from 'react'
import { type TimeRangeKey } from '../shared/insights'
import { INSIGHTS_CACHE_TTL_SECONDS } from '../shared/insights/utils/insightsUtils'
import { buildEmptyData, getDateRange, transformInsightsApiResponse } from '../shared/insights/utils/insightsTransforms'
import { useGetAdvisorInsightsDataQuery, useGetInsightsDataQuery } from './insightsDataApi'

interface UseInsightsDataOptions {
  enabled?: boolean
  userIdList?: string[]
  charts?: string[]
}

export const useInsightsData = (accountId: string, timeRange: TimeRangeKey, { enabled = true, userIdList, charts }: UseInsightsDataOptions = {}) => {
  const {
    data: apiData,
    isFetching,
    isLoading,
    isError,
  } = useGetInsightsDataQuery(
    { accountId, ...getDateRange(timeRange), userIdList: userIdList?.length ? userIdList : undefined, charts },
    { skip: !enabled || !accountId, refetchOnMountOrArgChange: INSIGHTS_CACHE_TTL_SECONDS },
  )

  const data = useMemo(() => transformInsightsApiResponse(apiData, timeRange), [apiData, timeRange])

  const displayData = data ?? (enabled && !isError ? buildEmptyData(timeRange) : null)

  return {
    data: isLoading ? null : displayData,
    isLoading,
    isFetching,
    isError,
  }
}

interface UseAdvisorInsightsDataOptions {
  enabled?: boolean
  meetingTemplateId?: string
  userIdList?: string[]
  charts?: string[]
}

export const ADVISOR_INSIGHTS_TIME_RANGE: TimeRangeKey = '30D'

export const useAdvisorInsightsData = (
  accountId: string,
  timeRange: TimeRangeKey,
  { enabled = true, meetingTemplateId, userIdList, charts }: UseAdvisorInsightsDataOptions = {},
) => {
  const {
    data: apiData,
    isFetching,
    isLoading,
    isError,
  } = useGetAdvisorInsightsDataQuery(
    { accountId, ...getDateRange(timeRange), meetingTemplateId, userIdList: userIdList?.length ? userIdList : undefined, charts },
    { skip: !enabled, refetchOnMountOrArgChange: true },
  )

  const data = useMemo(() => transformInsightsApiResponse(apiData, timeRange), [apiData, timeRange])

  const displayData = data ?? (enabled && !isError ? buildEmptyData(timeRange) : null)

  return {
    data: isLoading ? null : displayData,
    isLoading,
    isFetching,
    isError,
  }
}
